import { useState, useEffect, useRef, useCallback } from "react";
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  subMonths,
  addMonths,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isWithinInterval,
  isBefore,
  isAfter,
  startOfDay,
} from "date-fns";

interface DateRangePickerProps {
  startDate: Date;
  endDate: Date;
  onApply: (start: Date, end: Date) => void;
  onClose: () => void;
}

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

interface CalendarMonthProps {
  month: Date;
  selectedRange: { from: Date | null; to: Date | null };
  hoverDate: Date | null;
  onDateClick: (date: Date) => void;
  onDateHover: (date: Date | null) => void;
}

function CalendarMonth({ month, selectedRange, hoverDate, onDateClick, onDateHover }: CalendarMonthProps) {
  const monthStart = startOfMonth(month);
  const monthEnd = endOfMonth(month);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });

  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const getDateState = (date: Date) => {
    const { from, to } = selectedRange;
    if (!from) return { inRange: false, isStart: false, isEnd: false };

    const effectiveTo = to || hoverDate;

    if (!effectiveTo) {
      return { inRange: false, isStart: isSameDay(date, from), isEnd: false };
    }

    const rangeStart = isBefore(from, effectiveTo) ? from : effectiveTo;
    const rangeEnd = isAfter(from, effectiveTo) ? from : effectiveTo;

    const inRange = isWithinInterval(date, { start: startOfDay(rangeStart), end: startOfDay(rangeEnd) });
    const isStart = isSameDay(date, rangeStart);
    const isEnd = isSameDay(date, rangeEnd);

    return { inRange, isStart, isEnd };
  };

  // Group days into weeks
  const weeks: Date[][] = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }

  return (
    <div className="w-[280px]">
      {/* Month title */}
      <div className="text-center mb-4">
        <h3 className="text-base font-semibold text-foreground">
          {format(month, "MMMM yyyy")}
        </h3>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 mb-2">
        {WEEKDAYS.map((day) => (
          <div key={day} className="w-10 h-8 flex items-center justify-center text-xs font-medium text-muted-foreground">
            {day}
          </div>
        ))}
      </div>

      {/* Days grid - render by weeks for proper row backgrounds */}
      <div className="flex flex-col">
        {weeks.map((week, weekIdx) => {
          // Check if this week has any dates in range
          const weekHasRange = week.some(date => {
            const { inRange } = getDateState(date);
            return inRange && isSameMonth(date, month);
          });

          return (
            <div key={weekIdx} className="relative flex">
              {/* Row background for range */}
              {weekHasRange && (
                <div className="absolute inset-y-1 left-0 right-0 pointer-events-none">
                  {(() => {
                    // Find first and last in-range dates in this week
                    let firstInRange = -1;
                    let lastInRange = -1;
                    week.forEach((date, idx) => {
                      const { inRange } = getDateState(date);
                      if (inRange && isSameMonth(date, month)) {
                        if (firstInRange === -1) firstInRange = idx;
                        lastInRange = idx;
                      }
                    });

                    if (firstInRange === -1) return null;

                    const left = firstInRange * 40; // 40px per cell (w-10)
                    const width = (lastInRange - firstInRange + 1) * 40;

                    return (
                      <div
                        className="absolute top-0 bottom-0 bg-[var(--brand-primary)]/15 rounded-md"
                        style={{ left: `${left}px`, width: `${width}px` }}
                      />
                    );
                  })()}
                </div>
              )}

              {/* Day cells */}
              {week.map((date, dayIdx) => {
                const isCurrentMonth = isSameMonth(date, month);
                const { isStart, isEnd } = getDateState(date);
                const isSelected = isStart || isEnd;

                return (
                  <div
                    key={dayIdx}
                    className="relative w-10 h-10 flex items-center justify-center"
                    onMouseEnter={() => isCurrentMonth && onDateHover(date)}
                    onMouseLeave={() => onDateHover(null)}
                  >
                    <button
                      onClick={() => isCurrentMonth && onDateClick(date)}
                      disabled={!isCurrentMonth}
                      className={`
                        relative z-10 w-9 h-9 rounded-md text-sm font-medium transition-all
                        ${!isCurrentMonth ? "text-muted-foreground/30 cursor-default" : "cursor-pointer"}
                        ${isCurrentMonth && !isSelected ? "text-foreground hover:bg-muted" : ""}
                        ${isSelected && isCurrentMonth ? "bg-[var(--brand-primary)] text-white ring-2 ring-[var(--brand-primary)] ring-offset-2 ring-offset-card" : ""}
                      `}
                    >
                      {format(date, "d")}
                    </button>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function DateRangePicker({ startDate, endDate, onApply, onClose }: DateRangePickerProps) {
  const [selectedRange, setSelectedRange] = useState<{ from: Date | null; to: Date | null }>({
    from: startDate,
    to: endDate,
  });
  const [hoverDate, setHoverDate] = useState<Date | null>(null);
  const [leftMonth, setLeftMonth] = useState(() => startOfMonth(subMonths(endDate, 1)));
  const containerRef = useRef<HTMLDivElement>(null);

  const rightMonth = addMonths(leftMonth, 1);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    // Delay adding listener to avoid capturing the click that opened the picker
    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 10);
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const handleDateClick = useCallback((date: Date) => {
    setSelectedRange((prev) => {
      if (!prev.from || prev.to) {
        return { from: date, to: null };
      }
      if (isBefore(date, prev.from)) {
        return { from: date, to: prev.from };
      }
      return { from: prev.from, to: date };
    });
  }, []);

  const handleApply = () => {
    if (selectedRange.from) {
      onApply(selectedRange.from, selectedRange.to || selectedRange.from);
    }
  };

  return (
    <div
      ref={containerRef}
      className="absolute right-0 top-full mt-2 bg-card rounded-xl shadow-xl border border-border p-6 z-[100]"
    >
      {/* Header with navigation */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => setLeftMonth((prev) => subMonths(prev, 1))}
          className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <div className="flex-1" />

        <button
          onClick={() => setLeftMonth((prev) => addMonths(prev, 1))}
          className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Calendars */}
      <div className="flex gap-8">
        <CalendarMonth
          month={leftMonth}
          selectedRange={selectedRange}
          hoverDate={hoverDate}
          onDateClick={handleDateClick}
          onDateHover={setHoverDate}
        />
        <CalendarMonth
          month={rightMonth}
          selectedRange={selectedRange}
          hoverDate={hoverDate}
          onDateClick={handleDateClick}
          onDateHover={setHoverDate}
        />
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between mt-6 pt-5 border-t border-border">
        <div className="flex items-center gap-4">
          <div className="text-sm font-medium text-foreground">
            {selectedRange.from && (
              <>
                {format(selectedRange.from, "MMM d, yyyy")}
                {selectedRange.to && (
                  <span className="text-muted-foreground"> – </span>
                )}
                {selectedRange.to && format(selectedRange.to, "MMM d, yyyy")}
              </>
            )}
          </div>
          <button
            onClick={() => {
              const allTimeStart = new Date(2000, 0, 1);
              const today = new Date();
              setSelectedRange({ from: allTimeStart, to: today });
              setLeftMonth(startOfMonth(subMonths(today, 1)));
            }}
            className="text-xs text-[var(--brand-primary)] hover:text-[var(--brand-gradient-end)] font-medium transition-colors"
          >
            All Time
          </button>
        </div>
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            disabled={!selectedRange.from}
            className="px-5 py-2.5 text-sm font-medium text-white bg-[var(--brand-primary)] rounded-lg hover:bg-[var(--brand-gradient-end)] transition-colors disabled:opacity-50"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}
